import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { Box, Skeleton } from '@mui/material';
import { useInViewport } from '@acorn-portal/shared/src/components/telematics/common/use-in-viewport';
import { useGoogleMapsApi } from './use-google-maps-api';
import { buildCircleMarkerIcon, buildMarkerLabel, decodePolyline, fitMapToPoints, LatLng } from './trip-map-utils';

/**
 * Props for the TripMiniMap component.
 */
interface TripMiniMapProps {
	/** Encoded polyline string for the trip route. */
	polyline: string;
	/** Height of the map container in pixels. Default: 150 */
	height?: number;
}

/** How far outside the viewport a card can be and still keep a live map mounted. */
const VIEWPORT_MARGIN = '600px';
const MAP_PADDING = 20;
const ROUTE_COLOUR = '#000000';

const MAP_OPTIONS: google.maps.MapOptions = {
	disableDefaultUI: true,
	zoomControl: false,
	gestureHandling: 'none',
	clickableIcons: false,
	streetViewControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
	scaleControl: false,
	draggable: false,
};

/** Hide the Google footer/attribution for a cleaner preview. */
const HIDE_MAP_CHROME_SX = {
	'& .gm-style-cc': { display: 'none' },
	'& .gmnoprint': { display: 'none' },
	'& a[href^="https://maps.google.com/maps"]': { display: 'none !important' },
	'& .gm-style > div:last-child': { display: 'none' },
} as const;

interface MiniMapCanvasProps {
	path: LatLng[];
}

/**
 * The live Google Map. Mounted only while the card is near the viewport, so its
 * per-instance state (tiles loaded) resets naturally each time it re-mounts.
 */
const MiniMapCanvas: React.FC<MiniMapCanvasProps> = ({ path }) => {
	const [tilesLoaded, setTilesLoaded] = useState(false);
	const startPoint = path[0];
	const endPoint = path[path.length - 1];

	const onMapLoad = useCallback(
		(map: google.maps.Map) => {
			fitMapToPoints(map, path, MAP_PADDING);
			google.maps.event.addListenerOnce(map, 'tilesloaded', () => setTilesLoaded(true));
		},
		[path],
	);

	return (
		<>
			{/* Frosted overlay hides tile pop-in until the map has painted */}
			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					zIndex: 2,
					backgroundColor: 'rgba(245, 245, 245, 0.95)',
					backdropFilter: 'blur(8px)',
					pointerEvents: 'none',
					opacity: tilesLoaded ? 0 : 1,
					transition: 'opacity 0.3s ease-out',
				}}
			>
				<Skeleton variant='rectangular' width='100%' height='100%' animation='wave' sx={{ position: 'absolute' }} />
			</Box>

			<Box
				sx={{
					width: '100%',
					height: '100%',
					position: 'relative',
					zIndex: 1,
					opacity: tilesLoaded ? 1 : 0,
					transition: 'opacity 0.3s ease-in',
					...HIDE_MAP_CHROME_SX,
				}}
			>
				<GoogleMap
					mapContainerStyle={{ width: '100%', height: '100%' }}
					zoom={12}
					center={startPoint}
					onLoad={onMapLoad}
					options={MAP_OPTIONS}
				>
					<Polyline path={path} options={{ strokeColor: ROUTE_COLOUR, strokeOpacity: 1, strokeWeight: 3 }} />
					<Marker position={startPoint} label={buildMarkerLabel('A')} icon={buildCircleMarkerIcon(ROUTE_COLOUR)} />
					<Marker position={endPoint} label={buildMarkerLabel('B')} icon={buildCircleMarkerIcon(ROUTE_COLOUR)} />
				</GoogleMap>
			</Box>
		</>
	);
};

/**
 * Non-interactive mini map for trip card previews.
 *
 * Displays the trip route with start (A) and end (B) markers. All map
 * interactions are disabled. The live Google Map is mounted only while the
 * card is within the viewport (plus a margin) and is torn down again when it
 * scrolls away, so a long trip list never holds hundreds of map instances.
 *
 * @component
 * @example
 * ```tsx
 * <TripMiniMap polyline={trip.polyline} height={200} />
 * ```
 */
const TripMiniMap: React.FC<TripMiniMapProps> = ({ polyline, height = 150 }) => {
	const { isLoaded } = useGoogleMapsApi();
	const { ref: containerRef, isInViewport } = useInViewport({ rootMargin: VIEWPORT_MARGIN });

	const shouldRenderMap = isLoaded && isInViewport;
	const path = useMemo(() => (shouldRenderMap ? decodePolyline(polyline) : []), [shouldRenderMap, polyline]);

	return (
		<Box
			ref={containerRef}
			sx={{
				width: '100%',
				height,
				borderRadius: 1,
				overflow: 'hidden',
				position: 'relative',
				backgroundColor: 'grey.100',
			}}
		>
			{path.length > 0 ? <MiniMapCanvas path={path} /> : <Skeleton variant='rectangular' width='100%' height='100%' />}
		</Box>
	);
};

export default TripMiniMap;
